import React, { useEffect, useRef } from "react";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

const T = {
  navy: "#A80D2C",
  blue: "#C4163A",
  teal: "#7B0F2B",
  gold: "#D79A1E",
  slate: "#5B677A",
  bg: "#F5F8FC",
  card: "#FFFFFF",
  line: "#DCE5EF",
  ink: "#1D2733",
  orange: "#F97316",
};

// ── Static lookup tables (mirrored from AOResults.jsx) ───────────────────────

const AO_DEFINITIONS = [
  {
    key: "AR",
    label: "Achievement Related",
    tagline: '"I set my own goals and strive to achieve them."',
    overview:
      "Participants who score high on this aspect have a high level of Achievement Motivation. They set their own goals and try their best to achieve them. They love facing more and more challenges in their career and have a high level of reliability with regard to their work. These participants are the most important assets to the organisation for its growth.",
    orgNote:
      "It is difficult to hold these people in the organisation with only monetary incentives. After a certain level, salary takes a back seat in their list of priorities. The only way to hold them is to provide maximum opportunities of career development as well as personal development. They like freedom in their work and authority to take their own decisions. Given the freedom to work in their own style, they will not let the organisation down — they will exceed expectations.",
    toDo: [
      "Keep at it. Look for challenges.",
      "Create situations of challenge — otherwise you may get bored.",
      "Understand that due to your level of achievement motivation you will require freedom to work, which may or may not be possible in your work environment.",
      "However, do not reduce your level of achievement motivation.",
    ],
  },
  {
    key: "TR",
    label: "Task Related",
    tagline: '"Reliable, structured, and target-driven."',
    overview:
      "These people have a high level of reliability in their work. They make sure that the work allocated to them is completed within given timelines under any circumstances. They do not mind working under strict supervision and can be given targets. These people are important to organisations for their reliability.",
    orgNote:
      " ",
    toDo: [
      "Seek independence.",
      "Seek challenges and achieve your targets before time.",
      "Stretch your targets beyond the level given to you by your supervisor.",
    ],
  },
  {
    key: "UR",
    label: "Unrelated to Achievement",
    tagline: '"Comfort-zone focused, avoiding challenges."',
    overview:
      "These people are only concerned with their earnings. They are least interested in their work and would try to avoid challenges as far as possible. They do not set any goals for their career or their organisation. They are least reliable in their work life and, as a result, are most redundant in any organisation.",
    orgNote:
      "Remaining in the UR zone is a cause for concern. The first step is awareness — recognising this pattern and making a conscious decision to move toward challenge-seeking behaviour.",
    toDo: [
      "Take charge of your assignment.",
      "Try and create a winner's instinct — move away from the comfort zone. Initially look for mini challenges and mini stretch goals.",
      "Monitor your work and review the results actually obtained vs. the desired level of interest.",
      "Remaining in the UR zone is a cause for concern.",
    ],
  },
];

function getCategoryLabel(scores) {
  if (!scores) return "—";
  const { AR = 0, TR = 0, UR = 0 } = scores;  // ← destructure UR too
  const isHighAR = AR > 12;
  const isHighTR = TR >= 6;
  if (isHighAR) return "High AR";
  if (isHighTR && TR >= UR) return "High TR";  // ← only if TR actually dominates
  return "UR";
}

// ── Donut SVG ────────────────────────────────────────────────────────────────
function DonutSVG({ scores }) {
  const size = 360, cx = 140, cy = 180, r = 120, stroke = 36;
  const circumference = 2 * Math.PI * r;
  const keys = ["AR", "TR", "UR"];
  const colors = [T.orange, T.blue, T.gold];
  const total = keys.reduce((s, k) => s + (scores?.[k] ?? 0), 0) || 1;
  const displayLabel = getCategoryLabel(scores);

  let offset = 0;
  const segments = keys.map((key, i) => {
    const frac = (scores?.[key] ?? 0) / total;
    const dash = frac * circumference;
    const seg = { key, color: colors[i], dash, gap: circumference - dash, offset: offset * circumference };
    offset += frac;
    return seg;
  });

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ display: "block", margin: "0 auto", overflow: "visible" }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#EAF0F6" strokeWidth={stroke} />
      {segments.map(({ key, color, dash, gap, offset: off }) => (
        <circle
          key={key}
          cx={cx} cy={cy} r={r}
          fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${dash} ${gap}`}
          strokeDashoffset={circumference / 4 - off}
          strokeLinecap="butt"
        />
      ))}
      <text x={cx} y={cy - 14} textAnchor="middle" fontSize="13" fill={T.slate} fontWeight="600" style={{ fontFamily: "Arial, sans-serif" }}>
        PROFILE
      </text>
      <text x={cx} y={cy + 18} textAnchor="middle" fontSize="20" fontWeight="800" fill={T.navy} style={{ fontFamily: "Arial, sans-serif" }}>
        {displayLabel}
      </text>
    </svg>
  );
}

function formatDate(date) {
  return date.toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function HiddenAOReport({ participantData, onComplete }) {
  const containerRef = useRef(null);
  const hasGenerated = useRef(false);

  // ── Load html2canvas + jsPDF once ─────────────────────────────────────────
  useEffect(() => {
    let pending = 2;
    const done = () => { pending--; };
    const load = (src, id) => {
      if (document.getElementById(id)) { done(); return; }
      const s = document.createElement("script");
      s.id = id;
      s.src = src;
      s.onload = done;
      s.onerror = done;
      document.head.appendChild(s);
    };
    load("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js", "h2c-ao-v1");
    load("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js", "jspdf-ao-v1");
  }, []);

  // ── Generate PDF whenever participantData is set ──────────────────────────
  useEffect(() => {
    if (!participantData || hasGenerated.current) return;

    hasGenerated.current = true;
    const generate = async () => {
      // Wait for both scripts
      await new Promise((res) => {
        const check = () => {
          if (window.html2canvas && window.jspdf?.jsPDF) { res(); return; }
          setTimeout(check, 100);
        };
        check();
      });

      // Allow fonts + gradients to fully paint
      await new Promise((r) => setTimeout(r, 1400));

      const el = containerRef.current;
      if (!el) { onComplete?.(); return; }

      try {
        const { jsPDF } = window.jspdf;
        const A4_W = 210;
        const A4_H = 297;

        const pageEls = Array.from(el.querySelectorAll(".ao-page"));
        const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

        for (let i = 0; i < pageEls.length; i++) {
          const canvas = await window.html2canvas(pageEls[i], {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: "#ffffff",
            width: pageEls[i].scrollWidth,
            height: pageEls[i].scrollHeight,
            windowWidth: pageEls[i].scrollWidth,
          });

          const imgData = canvas.toDataURL("image/jpeg", 0.95);
          const logicalW = canvas.width / 2;
          const logicalH = canvas.height / 2;
          const MM_PER_PX = 25.4 / 96;
          const naturalW = logicalW * MM_PER_PX;
          const naturalH = logicalH * MM_PER_PX;
          const scaleToW = A4_W / naturalW;
          const projectedH = naturalH * scaleToW;

          let imgW, imgH, xOff, yOff;
          if (projectedH <= A4_H) {
            imgW = A4_W; imgH = projectedH; xOff = 0; yOff = 0;
          } else {
            const scaleToH = A4_H / naturalH;
            imgW = naturalW * scaleToH; imgH = A4_H;
            xOff = (A4_W - imgW) / 2; yOff = 0;
          }

          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, "JPEG", xOff, yOff, imgW, imgH);
        }

        const safeName = (participantData.userInfo?.name || "Report").replace(/\s+/g, "_");
        pdf.save(`AO_Report_${safeName}.pdf`);
      } catch (err) {
        console.error("AO PDF generation failed", err);
      } finally {
        hasGenerated.current = false;
        onComplete?.();

      }
    };

    generate();
  }, [participantData]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!participantData) return null;

  // ── Derive all display values ──────────────────────────────────────────────
  const {
    userInfo = {},
    scores = {},
    categoryProfile = "",
    insightText = "",
    submission_time,
  } = participantData;

  const participant = userInfo.name || "—";
  const assessmentDate = submission_time ? formatDate(new Date(submission_time)) : "—";
  const displayLabel = getCategoryLabel(scores);

  const categoriesForDisplay = AO_DEFINITIONS.map((def) => ({
    def,
    score: scores[def.key] ?? null,
    isDominant: (categoryProfile || "").includes(def.key),
  }));

  // Keys implied by the "Your Profile" label
  const profileKeys = displayLabel === "High AR" ? ["AR"]
    : displayLabel === "High TR" ? ["TR"]
      : displayLabel === "UR" ? ["UR"]
        : [];

  const categoriesByDominance = [...categoriesForDisplay]
    .map(c => ({ ...c, isDominant: profileKeys.includes(c.def.key) }))
    .sort((a, b) => {
      const aDom = profileKeys.includes(a.def.key);
      const bDom = profileKeys.includes(b.def.key);
      if (aDom === bDom) return (b.score ?? 0) - (a.score ?? 0);
      return aDom ? -1 : 1;
    });

  const totalScore = 22; // fixed: 22 questions total, each scored against exactly one dimension

  const thresholds = [
    { key: "AR", label: "Achievement Related", target: "> 12", pass: (scores.AR ?? 0) > 12, value: scores.AR },
    { key: "TR", label: "Task Related", target: "< 6", pass: (scores.TR ?? 0) < 6, value: scores.TR },
    { key: "UR", label: "Unrelated to Achievement", target: "< 4", pass: (scores.UR ?? 0) < 4, value: scores.UR },
  ];

  // ── Ideal-threshold bar data (Page 3 dashboard) ──────────────────────────
  const idealCaption = (score, threshold, direction, pass) => {
    if (score === threshold) {
      return direction === "above" ? "At the threshold — not yet above it" : "At the threshold — not yet under it";
    }
    if (pass) {
      return direction === "above" ? `${score - threshold} points above ideal` : `${threshold - score} points below ideal`;
    }
    return direction === "above" ? `${threshold - score} points below ideal` : `${score - threshold} points above ideal`;
  };

  const idealBars = [
    { key: "AR", label: "Achievement Related", color: T.orange, threshold: 12, direction: "above" },
    { key: "TR", label: "Task Related", color: T.blue, threshold: 6, direction: "below" },
    { key: "UR", label: "Unrelated to Achievement", color: T.gold, threshold: 4, direction: "below" },
  ].map(({ key, label, color, threshold, direction }) => {
    const score = scores[key] ?? 0;
    const pass = direction === "above" ? score > threshold : score < threshold;
    return {
      key, label, color, threshold, direction, score, pass,
      fillPct: Math.min(100, (score / totalScore) * 100),
      markerPct: Math.min(100, (threshold / totalScore) * 100),
      caption: idealCaption(score, threshold, direction, pass),
    };
  });

  const dominantCat = categoriesByDominance[0];
  const secondaryCat = categoriesByDominance[1];

  const LEAN_PHRASE = {
    AR: "a self-directed, challenge-seeking orientation — you're currently setting your own goals and pushing for stretch rather than waiting to be asked",
    TR: "a structured, reliability-first orientation — you're currently focused on delivering what's assigned within supervision rather than setting your own targets",
    UR: "a comfort-zone orientation — you're currently more focused on consistency and earnings than on setting or chasing your own goals",
  };
  const SECONDARY_PHRASE = {
    AR: "a genuine streak of self-directed ambition",
    TR: "a genuine streak of reliability",
    UR: "a mild pull toward comfort and consistency",
  };

  const profileParagraph = dominantCat && secondaryCat
    ? `Your responses lean most toward ${LEAN_PHRASE[dominantCat.def.key]} — with ${SECONDARY_PHRASE[secondaryCat.def.key]} close behind. This isn't a fixed trait: it's where your current pattern sits, and it can shift with awareness and deliberate practice. Full detail on your dominant and secondary orientations, and the third, follows on the next page.`
    : "";

  const passCount = thresholds.filter((t) => t.pass).length;
  const gapNoteText = passCount === 0
    ? "None of your three scores currently sit within the ideal range for a high achievement orientation — that's common, and it's exactly what this report is meant to surface. Review this alongside a manager or coach: the goal isn't to label where you stand today, but to identify how to channel your energy toward more self-directed goals over time."
    : passCount === 3
      ? "All three of your scores currently sit within the ideal range for a high achievement orientation — a strong, well-rounded profile. Review this alongside a manager or coach to keep building on this foundation and sustain it over time."
      : `${passCount} of your three scores currently sit within the ideal range for a high achievement orientation. Review this alongside a manager or coach: the goal isn't to label where you stand today, but to identify how to channel your energy toward more self-directed goals over time.`;

  const BAR_FILL = `linear-gradient(90deg,${T.navy},${T.blue},${T.gold})`;
  const BAR_FILL_DOMINANT = `linear-gradient(90deg,${T.gold},#e8b84b)`;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        top: "0",
        left: "-9999px",
        width: "1100px",
        opacity: 0,
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      {/* Font */}
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap"
        rel="stylesheet"
      />

      {/* ── All CSS scoped under .ao-pdf-root ── */}
      <style>{`
        .ao-pdf-root {
          font-family: 'Inter', Arial, sans-serif;
          background: #F5F8FC;
          color: #1D2733;
          line-height: 1.55;
        }
        .ao-pdf-root .report { max-width:1100px; margin:0 auto; padding:1.5rem 1rem 3rem; }

        /* ── Page shell ── */
        .ao-pdf-root .ao-page {
          background: #ffffff;
          border: 1px solid #DCE5EF;
          border-radius: 24px;
          box-shadow: 0 14px 36px rgba(11,31,58,.10);
          overflow: hidden;
          margin-bottom: 1.5rem;
        }

        /* ── Cover ── */
        .ao-pdf-root .cover { min-height:1556px; position:relative; color:white; }
        .ao-pdf-root .cover-inner { padding:3.5rem; display:flex; flex-direction:column; justify-content:space-between; min-height:1556px; }
        .ao-pdf-root .brand { display:flex; align-items:center; justify-content:space-between; gap:1rem; }
        .ao-pdf-root .brand-left { display:flex; align-items:center; gap:1rem; }
        .ao-pdf-root .logo { width:60px; height:60px; border-radius:16px; display:grid; place-items:center; font-weight:900; font-size:1.1rem; letter-spacing:.06em; flex-shrink:0; }
        .ao-pdf-root .brand h1 { margin:0; font-size:1.2rem; letter-spacing:.07em; text-transform:uppercase; }
        .ao-pdf-root .brand p  { margin:.15rem 0 0; color:rgba(255,255,255,.72); font-size:0.85rem; }
        .ao-pdf-root .badge { border:1px solid rgba(255,255,255,.2); background:rgba(255,255,255,.1); padding:.5rem 1rem; border-radius:999px; font-size:0.76rem; }
        .ao-pdf-root .cover-title { width:100%; }
        .ao-pdf-root .cover-title h2 {margin:0 0 1rem; font-size:3.4rem;line-height:.95; color:#FFFFFF;white-space:nowrap; max-width:none; border-left: 6px solid #FFFFFF !important; padding-left: 1rem;}
        .ao-pdf-root .cover-title p  { font-size:1.2rem; color:rgba(255,255,255,.82); width:100%; box-sizing:border-box; max-width100%; text-align:justify;}
        .ao-pdf-root .cover-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:2.5rem; }
        .ao-pdf-root .meta { background:rgba(255,255,255,.1);font-size: 1.2rem; border:1px solid rgba(255,255,255,.14); border-radius:16px; padding:.9rem 1rem; }
        .ao-pdf-root .meta span   { display:block; font-size:0.75rem; color:rgba(255,255,255,.65); margin-bottom:.2rem; }
        .ao-pdf-root .meta strong { font-size:0.95rem; color:#FFFFFF; }
        .ao-pdf-root .cover-badges { display:flex; flex-wrap:wrap; gap:.6rem; margin-top:2rem;}
        .ao-pdf-root .cover-badge  { background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.18); padding:.5rem .9rem; border-radius:999px; font-size:0.95rem; }
        .ao-pdf-root .footer-note { display:flex; justify-content:space-between; gap:1rem; color:rgba(255,255,255,.68); font-size:0.75rem; margin-top:2rem; }
.ao-pdf-root .logo-panel {
          position: absolute;
          top: 0;
          right: 0;
          width: 500px;
          height: 250px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1;
        }
.ao-pdf-root .logo-panel-inner {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
          padding-left: 60px;
          padding-bottom: 25px;
        }
          .ao-pdf-root .logo-img { height: 65px; width: auto; object-fit: contain; flex-shrink: 0; }

        /* ── Inner pages ── */
        .ao-pdf-root .page-inner { padding:2.5rem; }
        .ao-pdf-root .topbar { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; margin-bottom:1.75rem; }
        .ao-pdf-root .section-kicker { font-size:0.72rem; text-transform:uppercase; letter-spacing:.12em; font-weight:700; }
        .ao-pdf-root .section-title { margin:.35rem 0 0; font-size:clamp(1.45rem,1.2rem + 1.1vw,2rem); color:#0f2744; border-left: 5px solid #C4163A; padding-left: .75rem; }

        /* ── Cards ── */
        .ao-pdf-root .card { background:#ffffff; border:1px solid #DCE5EF; border-radius:18px; padding:1.25rem; box-shadow:0 4px 14px rgba(16,24,40,.05); }
        .ao-pdf-root .card h3 { margin:0 0 .65rem; font-size:1.05rem; color:#1D2733; }
        .ao-pdf-root .lead { color:#334155; font-size:0.93rem; margin:0; }

        /* ── Inner sub-panel ── */
        .ao-pdf-root .sub-panel { background:#fbfdff; border:1px solid #DCE5EF; border-radius:14px; padding:1rem; }
        .ao-pdf-root .sub-panel h4 { margin:0 0 .6rem; font-size:.95rem; color:#1D2733; }

        /* ── Score tiles ── */
        .ao-pdf-root .score-tile-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:.8rem; margin-bottom:.85rem; }
        .ao-pdf-root .score-tile { border-radius:14px; border:1px solid #DCE5EF; padding:.85rem; background:linear-gradient(180deg,#fff,#f8fbff); }
        .ao-pdf-root .score-tile.dominant { border:2px solid ${T.gold}; background:linear-gradient(180deg,#fffdf0,#fffbea); }
        .ao-pdf-root .score-tile span   { display:block; font-size:0.72rem; letter-spacing:.08em; text-transform:uppercase; color:#5B677A; font-weight:700; margin-bottom:.2rem; }
        .ao-pdf-root .score-tile strong { font-size:1.85rem; color:#A80D2C; font-weight:700; display:block; }
        .ao-pdf-root .score-tile.dominant strong { color:${T.gold}; }
        .ao-pdf-root .score-tile .band  { font-size:0.72rem; font-weight:700; text-transform:uppercase; letter-spacing:.06em; margin-top:.2rem; }

        /* ── Bars ── */
        .ao-pdf-root .bar-row { display:grid; grid-template-columns:210px 1fr 48px; gap:10px; align-items:center; margin-bottom:.65rem; }
        .ao-pdf-root .bar-label { font-size:0.88rem; color:#1D2733; }
        .ao-pdf-root .bar-label.dominant { font-weight:700; }
        .ao-pdf-root .track { height:10px; background:#EAF0F6; border-radius:999px; overflow:hidden; }
        .ao-pdf-root .fill  { height:100%; border-radius:999px; }
        .ao-pdf-root .score-val { text-align:right; font-size:0.85rem; font-weight:700; color:#A80D2C; }
        .ao-pdf-root .score-val.dominant { color:${T.gold}; }

        /* ── Callout ── */
        .ao-pdf-root .callout { background:linear-gradient(180deg,#fff9e8,#fffdf2); border:1px solid #F2D58A; border-radius:14px; padding:.85rem 1rem; font-size:0.88rem; line-height:1.6; }

        /* ── Threshold cards ── */
        .ao-pdf-root .threshold-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:.85rem; margin:.75rem 0; }
        .ao-pdf-root .threshold-card { border-radius:14px; padding:.85rem 1rem; }
        .ao-pdf-root .threshold-card.pass { background:linear-gradient(180deg,#f0fff4,#e8fdf0); border:1px solid #86efac; }
        .ao-pdf-root .threshold-card.fail { background:linear-gradient(180deg,#fff0f0,#fdeaea); border:1px solid #fca5a5; }

        /* ── Profile / dimension cards ── */
        .ao-pdf-root .profile-card { border:1px solid #DCE5EF; border-radius:16px; padding:1rem; margin-bottom:.75rem; background:#fff; }
        .ao-pdf-root .profile-card.active { border-color:${T.gold}; box-shadow:0 0 0 2px rgba(215,154,30,.18); }
        .ao-pdf-root .profile-header { display:flex; align-items:center; gap:.85rem; margin-bottom:.6rem; }
        .ao-pdf-root .profile-dot { width:12px; height:12px; border-radius:50%; flex-shrink:0; }
        .ao-pdf-root .profile-meta strong { display:block; font-size:.97rem; color:#1D2733; }
        .ao-pdf-root .profile-meta em     { font-style:italic; font-size:.82rem; color:#5B677A; }
        .ao-pdf-root .profile-meta .thresholds { font-size:.75rem; color:#5B677A; margin-top:.15rem; }
        .ao-pdf-root .profile-card p { margin:0 0 .35rem; font-size:0.84rem; color:#334155; }
        .ao-pdf-root .your-tag { font-size:.72rem; font-weight:700; color:${T.gold}; margin-left:.5rem; }

        /* ── Two-col ── */
        .ao-pdf-root .two-col { display:grid; grid-template-columns:1fr 1fr; gap:1.2rem; }

        /* ── Analysis items ── */
        .ao-pdf-root .analysis-item { border:1px solid #DCE5EF; border-radius:16px; padding:1rem; margin-bottom:.75rem; background:#fff; }
        .ao-pdf-root .analysis-item h4 { margin:0 0 .5rem; font-size:.97rem; color:#1D2733; }
        .ao-pdf-root .analysis-item p  { margin:0 0 .4rem; font-size:0.84rem; color:#334155; }
        .ao-pdf-root .analysis-item ul { margin:.3rem 0 0; padding-left:18px; font-size:0.84rem; color:#334155; line-height:1.6; }

        /* ── Trio (concepts) cards — Page 2 ── */
        .ao-pdf-root .trio-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin:1.1rem 0; }
        .ao-pdf-root .trio-card { border:1px solid #DCE5EF; border-radius:16px; padding:1.1rem; background:#fff; }
        .ao-pdf-root .trio-dot-row { display:flex; align-items:center; gap:.5rem; margin-bottom:.3rem; }
        .ao-pdf-root .trio-dot { width:12px; height:12px; border-radius:50%; flex-shrink:0; }
        .ao-pdf-root .trio-card h3 { margin:0; font-size:1.2rem; color:#1D2733; }
        .ao-pdf-root .trio-sub { font-size:.72rem; color:#5B677A; text-transform:uppercase; letter-spacing:.08em; font-weight:700; margin-bottom:.55rem; }
        .ao-pdf-root .trio-quote { font-style:italic; font-size:.82rem; color:#334155; margin-bottom:.55rem; }
        .ao-pdf-root .trio-desc { font-size:.83rem; color:#334155; margin:0 0 .7rem; line-height:1.6; }
        .ao-pdf-root .trio-ideal { font-size:.75rem; color:#5B677A; border-top:1px solid #EAF0F6; padding-top:.55rem; }
        .ao-pdf-root .trio-ideal span { color:#1D2733; font-weight:700; }
        .ao-pdf-root .relation-note { background:#f8f4eb; border:1px solid rgba(215,154,30,.35); border-radius:14px; padding:1rem 1.1rem; font-size:.85rem; color:#1D2733; line-height:1.65; margin-top:1.1rem; }

        /* ── Ideal threshold bars — Page 3 dashboard ── */
        .ao-pdf-root .panel {
  border: 1px solid #DCE5EF;
  border-radius: 16px;
  padding: 1.4rem 1.6rem;
  background: #fbfdff;
  margin-bottom: 1.25rem;
  display: flow-root;
  height: auto;
  overflow: visible;
}.ao-pdf-root .panel h3 { margin:0 0 .25rem; font-size:1.05rem; color:#1D2733; }
        .ao-pdf-root .panel .cap { font-size:.82rem; color:#5B677A; margin-bottom:1.25rem; line-height:1.6; max-width:640px; }
        .ao-pdf-root .ideal-bar-row { margin-bottom:2.5rem; }
        .ao-pdf-root .ideal-bar-row:last-child { margin-bottom:2rem; }
        .ao-pdf-root .ideal-bar-row .head { display:flex; justify-content:space-between; align-items:baseline; margin-bottom:.5rem; }
        .ao-pdf-root .ideal-bar-row .name { font-size:.92rem; font-weight:800; color:#1D2733; }
        .ao-pdf-root .ideal-bar-row .name .full { font-weight:500; color:#5B677A; font-size:.78rem; }
        .ao-pdf-root .ideal-bar-row .val { font-size:1.15rem; font-weight:900; }
        .ao-pdf-root .ideal-track { position:relative; height:13px; background:#EAE0E1; border-radius:7px; margin-top:1.7rem; }
        .ao-pdf-root .ideal-fill { height:100%; border-radius:7px; }
        .ao-pdf-root .ideal-marker { position:absolute; top:-5px; width:2px; height:23px; background:#1D2733; }
        .ao-pdf-root .ideal-marker .mk-label { position:absolute; top:-18px; white-space:nowrap; font-size:.72rem; font-weight:800; color:#1D2733; transform:translateX(-50%); left:1px; }
        .ao-pdf-root .ideal-caption { display:flex; justify-content:space-between; margin-top:.5rem; font-size:.78rem; color:#5B677A; }

        .ao-pdf-root .profile-callout { background:linear-gradient(135deg,${T.teal},${T.blue}); color:#fff; border-radius:16px; padding:1.4rem 1.6rem; margin-bottom:1.25rem; }
        .ao-pdf-root .profile-callout .pc-k { font-size:.72rem; text-transform:uppercase; letter-spacing:.1em; opacity:.8; font-weight:700; }
        .ao-pdf-root .profile-callout .pc-v { font-size:1.4rem; font-weight:900; margin-top:2px; }
        .ao-pdf-root .profile-callout p { font-size:.86rem; line-height:1.6; margin-top:.85rem; opacity:.95; max-width:680px; }

        .ao-pdf-root .gap-note { background:#fff9e8; border:1px solid #F2D58A; border-radius:12px; padding:.9rem 1.2rem; font-size:.83rem; line-height:1.6; color:#6b4a20; }

        /* ── Back cover ── */
        .ao-pdf-root .back-cover { min-height:1556px; color:white; }
        .ao-pdf-root .back-cover .page-inner { min-height:1556px; display:flex; flex-direction:column; justify-content:space-between; }
        .ao-pdf-root .closing h2 { margin:0 0 .85rem; font-size:2.8rem; color:#FFFFFF; border-left: 6px solid #FFFFFF !important; padding-left: 1rem; }
        .ao-pdf-root .closing p  { max-width:100%ch; color:rgba(255,255,255,.82); font-size: 1.2rem;text-align:justify; }
        .ao-pdf-root .contact-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:1.75rem; }
        .ao-pdf-root .contact { background:rgba(193,24,24,.1); border:1px solid rgba(255,255,255,.16); border-radius:16px; padding:.9rem; }
        .ao-pdf-root .contact span   { display:block; color:rgba(255,255,255,.64); font-size:0.73rem; margin-bottom:.25rem; }
        .ao-pdf-root .contact strong { font-size:0.92rem; }
        .ao-pdf-root .small { font-size:0.73rem; color:#5B677A; }
        .ao-pdf-root .light { color:rgba(255,255,255,.66); }

        .ao-pdf-root .coaching-read {
  border: 1px solid #CFC5C5;
  border-radius: 18px;
  padding-top: 1.2rem;
  padding-right: 1.7rem;
  padding-bottom: 1.2rem;
  padding-left: 1.7rem;
  background: #fffdfd;
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
}

.ao-pdf-root .coaching-badge {
  display: inline-block;
  border: 1px solid #DED7D7;
  border-radius: 999px;
  padding: 0.28rem 0.75rem;
  font-size: 0.72rem;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: #5B677A;
  background: #fff;
  margin-bottom: 0.7rem;
}

.ao-pdf-root .coaching-read h3 {
  margin: 0 0 0.55rem;
  font-size: 1.05rem;
  font-weight: 900;
  color: #111827;
}

.ao-pdf-root .coaching-read p {
  margin: 0 0 1.15rem;
  font-size: 0.9rem;
  line-height: 1.65;
  color: #455468;
}

.ao-pdf-root .coaching-read p:last-child {
  margin-bottom: 0;
}

.ao-pdf-root .coaching-read strong {
  font-weight: 800;
  color: #334155;
}
      `}</style>

      <div className="ao-pdf-root">
        <main className="report">

          {/* ══════════════════════════════════════════
              PAGE 1 — COVER
          ══════════════════════════════════════════ */}
          <section className="ao-page cover">
            <div style={{
              background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,${T.navy},${T.blue} 55%,${T.teal})`,
              minHeight: "1556px",
              flexDirection: "column",
            }}>

              <div className="logo-panel">
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: "absolute", top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img
                    src={Acumen_25_Years_LOGO}
                    alt="Acumen Logo"
                    className="logo-img"
                  />
                </div>
              </div>

              <div className="cover-inner">
                {/* Brand bar */}
                <div className="brand">
                  <div className="badge">Confidential Individual Report</div>
                </div>

                {/* Title block */}
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, alignItems: "stretch", zIndex: 2 }}>
                  <div className="cover-title">
                    <h2>Achievement Orientation<br></br> Assessment</h2>
                    <p>
                      A standardised self-evaluation instrument designed by Prayag Mehta,
                      measuring your orientation across three dimensions — Achievement Related,
                      Task Related, and Unrelated to Achievement.
                    </p>
                  </div>

                  {/* Meta grid */}
                  <div className="cover-grid">
                    <div className="meta">
                      <span>Participant</span>
                      <strong>{participant}</strong>
                    </div>
                    <div className="meta">
                      <span>Assessment Date</span>
                      <strong>{assessmentDate}</strong>
                    </div>
                    <div className="meta">
                      <span>Report Type</span>
                      <strong>Individual Achievement Profile</strong>
                    </div>
                  </div>

                  {/* Section badges */}
                  <div className="cover-badges">
                    {["Executive Dashboard", "Score Breakdown", "The Three Dimensions", "Report Analysis"].map(b => (
                      <span key={b} className="cover-badge">{b}</span>
                    ))}
                  </div>
                </div>

                {/* Footer note */}
                <div className="footer-note">
                  <div>Based on the Achievement Orientation framework designed by Prayag Mehta.</div>
                  <div>Scored across 22 questions.</div>
                </div>

              </div>
            </div>
          </section>


          {/* ══════════════════════════════════════════
              PAGE 2 — BEFORE THE SCORES (THE THREE DIMENSIONS)
          ══════════════════════════════════════════ */}
          <section className="ao-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker" style={{ color: T.gold }}>Page 2</div>
                  <h2 className="section-title">The Three Dimensions Behind Every Score</h2>
                </div>
                <div className="small">AR · TR · UR</div>
              </div>

              <p style={{ color: T.slate, fontSize: ".93rem", margin: "0 0 .5rem" }}>
                The Achievement Orientation framework groups how people engage with work challenges into three patterns. Every question on the assessment counts toward one of these three — understanding what each one means first makes your own scores, on the following pages, much easier to read.
              </p>

              <div className="trio-grid">
                {[
                  { key: "AR", color: T.orange, sub: "Achievement Related", quote: '"I set my own goals and strive to achieve them."', desc: "Self-driven and challenge-seeking. Sets personal goals, welcomes stretch, and needs freedom more than supervision.", ideal: "score above 12" },
                  { key: "TR", color: T.blue, sub: "Task Related", quote: '"Reliable, structured, and target-driven."', desc: "Dependable and consistent. Delivers what's assigned, works well under supervision, but rarely pushes past the target given.", ideal: "score below 6" },
                  { key: "UR", color: T.gold, sub: "Unrelated to Achievement", quote: '"Comfort-zone focused, avoiding challenges."', desc: "Focused mainly on earnings rather than the work itself. Sets few goals and tends to avoid challenge where possible.", ideal: "score below 4" },
                ].map(({ key, color, sub, quote, desc, ideal }) => (
                  <div key={key} className="trio-card">
                    <div className="trio-dot-row">
                      <div className="trio-dot" style={{ background: color }} />
                      <h3>{key}</h3>
                    </div>
                    <div className="trio-sub">{sub}</div>
                    <div className="trio-quote">{quote}</div>
                    <p className="trio-desc">{desc}</p>
                    <div className="trio-ideal">
                      Ideal for high achievement orientation: <span>{ideal}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="relation-note">
                Most people carry a mix of all three — the assessment identifies which one dominates your current responses. Later in this report, your three scores are shown against these same ideal thresholds, and your dominant orientation is explored in full, alongside the other two, on the pages that follow.
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 3 — YOUR SCORE DASHBOARD
          ══════════════════════════════════════════ */}
          <section className="ao-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker" style={{ color: T.gold }}>Page 3</div>
                  <h2 className="section-title">Your Score Dashboard</h2>
                </div>
                <div className="small">AO Score Summary · {participant}</div>
              </div>

              <p style={{ color: T.slate, fontSize: ".95rem", margin: "0 0 1.25rem" }}>
                Your responses across all 22 questions, scored against each dimension's ideal threshold for a high achievement orientation.
              </p>

              <div className="panel">
                <h3>Score Breakdown</h3>
                <p className="cap">
                  Each bar shows your score out of {totalScore}. The vertical marker shows the ideal threshold for that dimension — for AR you want to be past it; for TR and UR you want to stay short of it.
                </p>

                {idealBars.map(({ key, label, color, threshold, direction, score, fillPct, markerPct, caption }) => (
                  <div className="ideal-bar-row" key={key}>
                    <div className="head">
                      <span className="name">{key} <span className="full">— {label}</span></span>
                      <span className="val" style={{ color }}>{score}<span style={{ fontSize: "12px", fontWeight: 600, color: T.slate }}>/{totalScore}</span></span>
                    </div>
                    <div className="ideal-track">
                      <div className="ideal-fill" style={{ width: `${fillPct}%`, background: color }} />
                      <div className="ideal-marker" style={{ left: `${markerPct}%` }}>
                        <span className="mk-label">Ideal: {direction === "above" ? ">" : "<"} {threshold}</span>
                      </div>
                    </div>
                    <div className="ideal-caption">
                      <span>Your score: {score}</span>
                      <span>{caption}</span>
                    </div>
                  </div>
                ))}
              </div>

              {dominantCat && secondaryCat && (
                <div className="profile-callout">
                  <div className="pc-k">Your Dominant Orientation</div>
                  <div className="pc-v">{dominantCat.def.key} — {dominantCat.def.label}</div>
                  <div className="pc-k" style={{ marginTop: 14 }}>Your Secondary Orientation</div>
                  <div className="pc-v" style={{ fontSize: "1.1rem" }}>{secondaryCat.def.key} — {secondaryCat.def.label}</div>
                  <p>{profileParagraph}</p>
                </div>
              )}

              <div className="gap-note">
                {gapNoteText}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 4 — YOUR ORIENTATION
          ══════════════════════════════════════════ */}
          <section className="ao-page">
            <div className="page-inner" style={{ padding: "1.5rem 1.25rem 0" }}>
              <div className="topbar" style={{padding: "0 0 0 0"}}>
                <div>
                  <div className="section-kicker" style={{ color: T.gold }}>Page 4</div>
                  <h2 className="section-title">Your Orientation</h2>
                </div>
                <div className="small">AR · TR · UR</div>
              </div>

              <p style={{ color: T.slate, fontSize: ".88rem", margin: "0 0 1.1rem" }}>
                Each dimension reflects a distinct pattern of how you perceive challenges, engage with your work environment, and direct your energy. All three are detailed below, ordered by your score — starting with your dominant orientation, followed by your secondary one.
              </p>

              {(() => {
                const KEY_COLOR = { AR: T.orange, TR: T.blue, UR: T.gold };
                const KEY_LIGHTBG = { AR: "#fff4ed", TR: "#eef2ff", UR: "#fffbea" };
                const cards = categoriesByDominance;
                return cards.map(({ def, score, isDominant }) => {
                  const color = KEY_COLOR[def.key];
                  const lightBg = KEY_LIGHTBG[def.key];
                  return (
                    <div key={def.key} style={{
                      border: isDominant ? `2px solid ${color}` : `1px solid ${T.slate}55`,
                      borderRadius: 16, marginBottom: ".75rem",
                      background: isDominant ? `linear-gradient(180deg,${lightBg},#fff)` : "#fff",
                      boxShadow: isDominant ? `0 4px 18px ${color}28` : "none",
                      overflow: "hidden",
                    }}>
                      {/* Header */}
                      <div style={{ padding: "1rem 1.25rem .75rem" }}>
                        <div style={{ fontWeight: 700, color, fontSize: ".97rem", marginBottom: 6 }}>
                          {def.key} — {def.label}
                          {isDominant && (
                            <span style={{ marginLeft: 8, fontSize: ".72rem", background: color, color: "#fff", borderRadius: 999, padding: "2px 8px", verticalAlign: "middle", fontWeight: 700 }}>
                              DOMINANT
                            </span>
                          )}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 9, height: 9, borderRadius: "50%", background: T.slate, flexShrink: 0 }} />
                          <em style={{ fontSize: ".82rem", color: T.slate }}>{def.tagline}</em>
                        </div>
                        {score !== null && (
                          <div style={{ fontSize: ".75rem", color: T.slate, marginTop: 3, paddingLeft: 17 }}>
                            Score: {score ?? "—"} / {totalScore}
                          </div>
                        )}
                      </div>
                      {/* Body */}
                      <div style={{ padding: "0 1.25rem 1rem", borderTop: `1px solid ${T.line}`, paddingTop: ".75rem" }}>
                        <p style={{ margin: "0 0 .5rem", fontSize: ".84rem", color: T.ink, lineHeight: 1.65 }}>{def.overview}</p>
                        {def.orgNote && <p style={{ margin: "0 0 .5rem", fontSize: ".82rem", color: T.slate, fontStyle: "italic", lineHeight: 1.6 }}>{def.orgNote}</p>}
                        <strong style={{ fontSize: ".85rem", color }}>To do:</strong>
                        <ul style={{ marginTop: ".45rem", paddingLeft: "18px"}}>
                          {def.toDo.map((item, index) => (
                            <li key={index} style={{ marginBottom: ".35rem", color: T.ink, fontSize: ".84rem", lineHeight: 1.6 }}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  );
                });
              })()}
              <div className="coaching-read">
                <div className="coaching-badge">
                  COACHING READ — NOT A SEPARATE SCORE
                </div>

                <h3>
                  Your Combination: {dominantCat?.def.key} + {secondaryCat?.def.key}
                </h3>

                <p>
                  Your two highest scores are{" "}
                  <strong>
                    {dominantCat?.def.label} ({dominantCat?.score})
                  </strong>{" "}
                  and{" "}
                  <strong>
                    {secondaryCat?.def.label} ({secondaryCat?.score})
                  </strong>
                  {" "}— a strong pull toward comfort and consistency, paired with genuine
                  reliability once work is assigned. In practice, this often looks like
                  steady, dependable execution with little self-initiated stretch; work
                  gets done well and on time, but mostly within the boundaries someone else
                  has set.
                </p>

                <p>
                  That reliability is a real asset, and it's also the easiest lever to
                  build from — since the structure and dependability of{" "}
                  {secondaryCat?.def.key} are already there, the more direct path forward
                  isn't to change how you work, but to start setting a few goals of your own
                  inside it rather than waiting for them to be assigned.
                </p>
              </div>

              {/* Context box */}
              {/* <div style={{ background: "#f8f4eb", border: `1px solid ${T.gold}55`, borderRadius: 14, padding: "1rem", marginTop: ".5rem" }}>
                <div style={{ fontSize: ".72rem", color: T.gold, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: ".5rem" }}>
                  Context &amp; Meaning
                </div>
                <p style={{ margin: 0, fontSize: ".83rem", color: T.ink, lineHeight: 1.6 }}>
                  These dimensions are not fixed traits — they reflect current orientations that can shift with awareness, intentional effort, and the right environmental conditions. Understanding where you sit today is the first step to deliberate growth.
                </p>
              </div> */}
            </div>
          </section>


          {/* ══════════════════════════════════════════
              BACK COVER
          ══════════════════════════════════════════ */}
          <section className="ao-page back-cover">
            <div style={{
              background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,#A81026,#C4163A 55%,#7B0F2B)`,
              minHeight: "1556px",
            }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1.5rem 2.5rem",
                borderBottom: "1px solid rgba(255,255,255,0.15)",
              }}>
                <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: ".12em", fontWeight: 700, color: "rgba(255,255,255,0.66)" }}>
                  {/* Back Cover */}
                </div>
                <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", letterSpacing: ".05em" }}>
                  Acumen Business Catalyst
                </div>
              </div>
              <div className="page-inner">
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                  <div className="closing">
                    <h2>Activating Your Achievement Potential</h2>
                    <p>
                      Achievement orientation is not fixed. It is a dynamic that responds to awareness,
                      deliberate practice, and the right environmental conditions. Acumen Business Catalyst
                      uses assessments like this to turn behavioural data into focused development
                      conversations that drive lasting change.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="contact-grid">
                    <div className="contact">
                      <span>Brand</span>
                      <strong>Acumen Business Catalyst</strong>
                    </div>
                    <div className="contact">
                      <span>Focus</span>
                      <strong>Leadership • Culture • Capability</strong>
                    </div>
                    <div className="contact">
                      <span>Report type</span>
                      <strong>Individual Achievement Profile</strong>
                    </div>
                  </div>
                  <p className="light" style={{ marginTop: "1.5rem", fontSize: "0.75rem" }}>
                    This report is generated from the Achievement Orientation (AO) assessment framework, designed by Prayag Mehta. © 2026 Acumen Business Catalyst.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}